import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Button, Table, Pagination, Badge, Spinner, Alert, Modal, Form } from '@themesberg/react-bootstrap';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';

export default () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingRequestId, setClosingRequestId] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(
        collection(db, 'contactRequests'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const requestsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sadece pending olanları göster
      const pendingRequests = requestsList.filter(r => r.status === 'pending');
      setRequests(pendingRequests);
    } catch (err) {
      console.error("Error loading contact requests:", err);
      setError("Talepler yüklenirken hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        (r.userName && r.userName.toLowerCase().includes(searchLower)) ||
        (r.userEmail && r.userEmail.toLowerCase().includes(searchLower)) ||
        (r.subject && r.subject.toLowerCase().includes(searchLower))
      );
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  const handleCloseRequest = async () => {
    if (!closingRequestId) return;
    
    try {
      await deleteDoc(doc(db, 'contactRequests', closingRequestId));
      setRequests(requests.filter(r => r.id !== closingRequestId));
      setShowCloseModal(false);
      setClosingRequestId(null);
    } catch (err) {
      console.error("Error closing request:", err);
      alert("Talep kapatılırken hata oluştu: " + err.message);
    }
  };

  const handleCloseClick = (requestId) => {
    setClosingRequestId(requestId);
    setShowCloseModal(true);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('tr-TR');
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Talepler</h4>
          <p className="mb-0">Kullanıcılardan gelen talepler</p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="pt-0">
          <Row className="justify-content-between align-items-center mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="İsim, e-posta veya konu ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Henüz talep bulunmuyor.</p>
            </div>
          ) : (
            <>
              <Table hover className="user-table align-items-center">
                <thead>
                  <tr>
                    <th className="border-bottom">#</th>
                    <th className="border-bottom">Kullanıcı</th>
                    <th className="border-bottom">E-posta</th>
                    <th className="border-bottom">Konu</th>
                    <th className="border-bottom">Tarih</th>
                    <th className="border-bottom">Durum</th>
                    <th className="border-bottom">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((request, index) => (
                    <tr key={request.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{request.userName || '-'}</td>
                      <td>{request.userEmail || '-'}</td>
                      <td>{request.subject || '-'}</td>
                      <td>{formatDate(request.createdAt)}</td>
                      <td>
                        <Badge bg={request.status === 'pending' ? 'warning' : 'success'}>
                          {request.status === 'pending' ? 'Bekliyor' : 'Kapatıldı'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2"
                          onClick={() => handleViewRequest(request)}
                        >
                          <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                          Oku
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleCloseClick(request.id)}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          Kapat
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* View Request Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Talep Detayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Kullanıcı:</strong> {selectedRequest.userName || '-'}
                </Col>
                <Col md={6}>
                  <strong>E-posta:</strong> {selectedRequest.userEmail || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Konu:</strong> {selectedRequest.subject || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Açıklama:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    {selectedRequest.description || '-'}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <strong>Tarih:</strong> {formatDate(selectedRequest.createdAt)}
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Close Request Confirmation Modal */}
      <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Talep Kapat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu talebi kapatmak istediğinize emin misiniz? Bu işlem kalıcıdır ve talep silinecektir.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCloseModal(false);
            setClosingRequestId(null);
          }}>
            Vazgeç
          </Button>
          <Button variant="danger" onClick={handleCloseRequest}>
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            Talebi Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

